import React, { useState, useEffect, useCallback, ChangeEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';

import { accountabilityAPI } from '@/services/accountabilityAPI';
import {
  WorkerAccountabilityViolationV2,
  ViolationFiltersV2,
  Pagination,
  ViolationDisplayStatus,
} from '@/types/accountability';

import {
  faExclamationTriangle,
  faEllipsisVertical,
} from '@fortawesome/free-solid-svg-icons';
import { faCheckCircle } from '@fortawesome/free-regular-svg-icons';

import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  SelectChangeEvent,
} from '@mui/material';

import Alert from '@/components/Alert';
import Card from '@/components/Card';
import LoadingState from '@/components/LoadingState';
import Page from '@/components/Page';
import Paginator from '@/components/Paginator/Paginator';
import Stack from '@/components/Stack';
import Table from '@/components/Table';
import TableHeader from '@/components/TableHeader';
import TextStack from '@/components/TextStack';
import { Body, Small } from '@/components/Typography';
import Button from '@/components/Button';
import Avatar from '@/components/Avatar';
import Link from '@/elements/Link';
import { TableCell, TableRow } from '@/elements/Table';
import DropdownMenu from '@/components/DropdownMenu';
import styled from '@/styles';

import ViolationActionModal from '../shared/ViolationActionModal';
import {
  getDisplayStatusBadge,
  getViolationTypeLabel,
} from '../shared/violationBadges';

const headerFields = [
  'User Information',
  'Violation Type',
  'Job ID',
  'Timestamp',
  'Status',
  'File Attachment',
  '',
];

const truncateFilename = (name: string, maxLength = 20): string => {
  if (name.length <= maxLength) return name;
  const dotIndex = name.lastIndexOf('.');
  const ext = dotIndex > -1 ? name.slice(dotIndex) : '';
  const baseName = dotIndex > -1 ? name.slice(0, dotIndex) : name;
  const truncatedBase = baseName.slice(0, maxLength - ext.length - 3);
  return `${truncatedBase}...${ext}`;
};

const TabBar = styled('div', {
  display: 'inline-flex',
  backgroundColor: 'white',
  borderRadius: '$lg $lg 0 0',
  padding: '0 4px',
  borderBottom: '1px solid $colors$neutralLightest',
});

const TabLabel = styled('span', {
  display: 'inline-block',
  padding: '12px 24px',
  fontSize: '14px',
  fontWeight: 500,
  color: '$colors$textDefault',
  borderBottom: '2px solid $colors$themeDefault',
});

export const ViolationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [violations, setViolations] = useState<
    WorkerAccountabilityViolationV2[]
  >([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState(
    searchParams.get('search') || '',
  );
  const [canClearViolation, setCanClearViolation] = useState(false);

  const [filters, setFilters] = useState<ViolationFiltersV2>({
    page: parseInt(searchParams.get('page') || '1'),
    per_page: parseInt(searchParams.get('per_page') || '25'),
    search: searchParams.get('search') || undefined,
    violation_type:
      (searchParams.get('violation_type') as 'late_drop' | 'no_show') ||
      undefined,
    display_status:
      (searchParams.get('display_status') as ViolationDisplayStatus) ||
      undefined,
    sort_by:
      (searchParams.get('sort_by') as
        | 'occurred_at'
        | 'violation_type'
        | 'status') || 'occurred_at',
    sort_order: (searchParams.get('sort_order') as 'asc' | 'desc') || 'desc',
  });

  const [actionModal, setActionModal] = useState<{
    open: boolean;
    type: 'uphold' | 'clear';
    violation: WorkerAccountabilityViolationV2;
  } | null>(null);

  const updateSearchParams = useCallback(
    (newFilters: ViolationFiltersV2) => {
      const params = new URLSearchParams();
      Object.entries(newFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          params.set(key, String(value));
        }
      });
      setSearchParams(params);
    },
    [setSearchParams],
  );

  const loadViolations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await accountabilityAPI.getViolationsV2(filters);

      if (response.success) {
        setViolations(
          (response.violations || []) as WorkerAccountabilityViolationV2[],
        );
        setPagination(response.pagination || null);
      }
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load violations';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadViolations();
  }, [loadViolations]);

  useEffect(() => {
    let isMounted = true;

    const loadClearPermission = async () => {
      try {
        const response = await accountabilityAPI.getClearPermission();
        if (!isMounted) return;
        setCanClearViolation(
          !!(response.success && response.can_clear_violation),
        );
      } catch (error) {
        if (isMounted) {
          setCanClearViolation(false);
        }
      }
    };

    loadClearPermission();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleFilterChange = (newFilters: Partial<ViolationFiltersV2>) => {
    const updated = { ...filters, ...newFilters, page: 1 };
    setFilters(updated);
    updateSearchParams(updated);
  };

  const handleSearch = () => {
    handleFilterChange({ search: searchInput || undefined });
  };

  const handlePageChange = (pageNumber: number) => {
    const updated = { ...filters, page: pageNumber };
    setFilters(updated);
    updateSearchParams(updated);
  };

  const handleNumberItemsChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const updated = {
      ...filters,
      page: 1,
      per_page: parseInt(event.target.value),
    };
    setFilters(updated);
    updateSearchParams(updated);
  };

  const handleRowClick = (violationId: number) => {
    navigate(`/tasks/violations/${violationId}`);
  };

  const handleExport = async () => {
    try {
      const blob = await accountabilityAPI.exportViolations(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `violations_export_${format(new Date(), 'yyyyMMdd_HHmmss')}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to export violations');
    }
  };

  const handleActionSuccess = () => {
    setSuccess(
      actionModal?.type === 'clear'
        ? 'Violation cleared successfully'
        : 'Violation upheld successfully',
    );
    loadViolations();
    setActionModal(null);
  };

  const totalItems = pagination?.total_count || 0;

  const hasActiveFilters =
    searchInput || filters.violation_type || filters.display_status;

  return (
    <Page
      footer={
        violations.length > 0 &&
        pagination && (
          <Paginator
            currentPage={filters.page || 1}
            handleNumberItemsChange={handleNumberItemsChange}
            handlePageChange={handlePageChange}
            itemsLength={totalItems}
            itemsPerPage={filters.per_page || 25}
          />
        )
      }
      size="full"
      title="Tasks"
    >
      {actionModal && (
        <ViolationActionModal
          type={actionModal.type}
          violationId={actionModal.violation.id}
          workerName={actionModal.violation.worker_name}
          onClose={() => setActionModal(null)}
          onSuccess={handleActionSuccess}
        />
      )}

      <TabBar>
        <TabLabel>Violations</TabLabel>
      </TabBar>

      <Card>
        <Card.Section>
          <Stack justify="end">
            <Button
              a11yLabel="Manage violation approvers"
              appearance="outline"
              label="Manage Violation Approvers"
              onClick={() => navigate('/tasks/violation-approvers')}
            />
          </Stack>
        </Card.Section>
      </Card>

      {(error || success) && (
        <Card>
          <Card.Section>
            <Stack vertical gap={8}>
              {error && (
                <Alert
                  description={error}
                  icon={faExclamationTriangle}
                  status="danger"
                  title="Error"
                />
              )}
              {success && (
                <Alert
                  description={success}
                  icon={faCheckCircle}
                  status="success"
                  title="Success"
                />
              )}
            </Stack>
          </Card.Section>
        </Card>
      )}

      <Card>
        <Card.Section>
          <Stack vertical gap={16}>
            <Stack gap={16} style={{ flexWrap: 'wrap' }}>
              <TextField
                label="Search"
                placeholder="Search by name, email, phone..."
                size="small"
                sx={{ minWidth: 250, flex: 1 }}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Violation Type</InputLabel>
                <Select
                  label="Violation Type"
                  value={filters.violation_type || ''}
                  onChange={(e: SelectChangeEvent) =>
                    handleFilterChange({
                      violation_type:
                        (e.target.value as 'late_drop' | 'no_show') ||
                        undefined,
                    })
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="late_drop">Late Drop</MenuItem>
                  <MenuItem value="no_show">No Show</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={filters.display_status || ''}
                  onChange={(e: SelectChangeEvent) =>
                    handleFilterChange({
                      display_status:
                        (e.target.value as ViolationDisplayStatus) || undefined,
                    })
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="needs_review">Needs Review</MenuItem>
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="upheld">Upheld</MenuItem>
                  <MenuItem value="cleared">Cleared</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Sort By</InputLabel>
                <Select
                  label="Sort By"
                  value={filters.sort_by || 'occurred_at'}
                  onChange={(e: SelectChangeEvent) =>
                    handleFilterChange({
                      sort_by: e.target.value as
                        | 'occurred_at'
                        | 'violation_type'
                        | 'status',
                    })
                  }
                >
                  <MenuItem value="occurred_at">Timestamp</MenuItem>
                  <MenuItem value="violation_type">Violation Type</MenuItem>
                  <MenuItem value="status">Status</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            {hasActiveFilters && (
              <Stack gap={8} style={{ alignItems: 'center' }}>
                <Small color="lighter">Active filters:</Small>
                {searchInput && (
                  <Chip
                    label={`Search: ${searchInput}`}
                    size="small"
                    onDelete={() => {
                      setSearchInput('');
                      handleFilterChange({ search: undefined });
                    }}
                  />
                )}
                {filters.violation_type && (
                  <Chip
                    label={`Type: ${filters.violation_type === 'late_drop' ? 'Late Drop' : 'No Show'}`}
                    size="small"
                    onDelete={() =>
                      handleFilterChange({ violation_type: undefined })
                    }
                  />
                )}
                {filters.display_status && (
                  <Chip
                    label={`Status: ${filters.display_status === 'needs_review' ? 'Needs Review' : filters.display_status.charAt(0).toUpperCase() + filters.display_status.slice(1)}`}
                    size="small"
                    onDelete={() =>
                      handleFilterChange({ display_status: undefined })
                    }
                  />
                )}
                <Chip
                  color="primary"
                  label="Clear all"
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setFilters({
                      page: 1,
                      per_page: 25,
                      sort_by: 'occurred_at',
                      sort_order: 'desc',
                    });
                    setSearchInput('');
                    setSearchParams(new URLSearchParams());
                  }}
                />
              </Stack>
            )}
          </Stack>
        </Card.Section>
      </Card>

      <Card>
        <Table>
          <TableHeader fields={headerFields} />
          <tbody>
            {violations.map((violation) => (
              <TableRow key={violation.id}>
                <TableCell>
                  <Stack gap={12} align="center">
                    <Avatar
                      image={violation.worker_avatar_url || undefined}
                      name={violation.worker_name}
                      size="sm"
                    />
                    <TextStack spacing="tight">
                      <Link to={`/workers_v2/${violation.worker_id}`}>
                        <Body weight="medium">{violation.worker_name}</Body>
                      </Link>
                      <Body size="sm" color="light">
                        {violation.worker_email}
                      </Body>
                      <Body size="sm" color="light">
                        {violation.worker_phone}
                      </Body>
                    </TextStack>
                  </Stack>
                </TableCell>
                <TableCell
                  onClick={() => handleRowClick(violation.id)}
                  style={{ cursor: 'pointer' }}
                >
                  {getViolationTypeLabel(violation.violation_type)}
                </TableCell>
                <TableCell>
                  <Link
                    to={`/orders/${violation.order_id}/jobs/${violation.job_id}`}
                  >
                    #{violation.job_id}
                  </Link>
                </TableCell>
                <TableCell
                  onClick={() => handleRowClick(violation.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <TextStack spacing="tight">
                    <Body weight="medium">
                      {format(new Date(violation.occurred_at), 'MM/dd/yyyy')}
                    </Body>
                    <Body size="sm" color="light">
                      {format(new Date(violation.occurred_at), 'h:mm a')}
                    </Body>
                  </TextStack>
                </TableCell>
                <TableCell
                  onClick={() => handleRowClick(violation.id)}
                  style={{ cursor: 'pointer' }}
                >
                  {getDisplayStatusBadge(violation.display_status)}
                </TableCell>
                <TableCell>
                  {violation.file_attachment_url ? (
                    <Link to={violation.file_attachment_url} target="_blank">
                      {truncateFilename(
                        violation.file_attachment_name || 'Attachment',
                      )}
                    </Link>
                  ) : (
                    <Body color="light">-</Body>
                  )}
                </TableCell>
                <TableCell>
                  <DropdownMenu
                    justify="right"
                    actions={[
                      [
                        {
                          label: 'Review',
                          onAction: () =>
                            navigate(`/tasks/violations/${violation.id}`),
                        },
                        ...(violation.display_status === 'open' ||
                        violation.display_status === 'needs_review'
                          ? [
                              {
                                label: 'Uphold violation',
                                onAction: () =>
                                  setActionModal({
                                    open: true,
                                    type: 'uphold' as const,
                                    violation,
                                  }),
                              },
                              ...(canClearViolation
                                ? [
                                    {
                                      label: 'Clear violation',
                                      onAction: () =>
                                        setActionModal({
                                          open: true,
                                          type: 'clear' as const,
                                          violation,
                                        }),
                                    },
                                  ]
                                : []),
                            ]
                          : []),
                      ],
                    ]}
                  >
                    <Button
                      a11yLabel="Actions"
                      appearance="plain"
                      iconLeft={faEllipsisVertical}
                    />
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {loading && (
              <TableRow>
                <TableCell
                  colSpan={headerFields.length}
                  style={{ textAlign: 'center' }}
                >
                  <LoadingState overlayColor="white" />
                </TableCell>
              </TableRow>
            )}
            {!loading && violations.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={headerFields.length}
                  style={{ textAlign: 'center' }}
                >
                  <Stack vertical gap={8} align="center">
                    <Body>No violations found.</Body>
                    <Button
                      a11yLabel="Clear filters"
                      appearance="outline"
                      label="Clear Filters"
                      onClick={() => {
                        setFilters({
                          page: 1,
                          per_page: 25,
                          sort_by: 'occurred_at',
                          sort_order: 'desc',
                        });
                        setSearchInput('');
                        setSearchParams(new URLSearchParams());
                      }}
                    />
                  </Stack>
                </TableCell>
              </TableRow>
            )}
          </tbody>
        </Table>
      </Card>
    </Page>
  );
};

export default ViolationsPage;
